import React from 'react'
import { Button, ButtonProps, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { trackEvent } from 'analytics'

type HeaderExportButtonProps = Omit<ButtonProps, 'children'> & {
  children: React.ReactNode
  analyticsName: string
  icon?: string
  loading?: boolean
  loadingLabel?: React.ReactNode
  tooltipTitle?: React.ReactNode
}

const baseSx = (loading: boolean) => ({
  height: 30,
  minWidth: 0,
  px: 1.5,
  borderRadius: '24px',
  color: loading ? 'primary.main' : '#464646',
  fontWeight: 500,
  fontSize: 12,
  bgcolor: '#F7F7F7',
  display: 'flex',
  gap: 0.75,
  alignItems: 'center',
  textTransform: 'none',
  '&:hover': {
    bgcolor: '#F7F7F7',
    color: 'primary.main'
  }
})

const mergeSx = (sx: HeaderExportButtonProps['sx'], loading: boolean): ButtonProps['sx'] => {
  const customSx = Array.isArray(sx) ? sx : sx ? [sx] : []
  return [baseSx(loading), ...customSx] as ButtonProps['sx']
}

const HeaderExportButton = ({
  children,
  analyticsName,
  icon = 'arrow-up-from-line',
  loading = false,
  loadingLabel,
  size = 'small',
  sx,
  tooltipTitle,
  onClick,
  ...props
}: HeaderExportButtonProps) => {
  const { t } = useTranslation()
  const resolvedTooltipTitle = tooltipTitle ?? t('export.tooltip')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!loading) trackEvent('export_button_clicked', { export_name: analyticsName })
    onClick?.(event)
  }

  const button = (
    <Button
      {...props}
      size={size}
      sx={mergeSx(sx, loading)}
      onClick={handleClick}
    >
      <i className={`fa-solid fa-${loading ? 'spinner fa-spin' : icon}`} style={{ fontSize: 11 }} />
      {loading && loadingLabel ? loadingLabel : children}
    </Button>
  )

  if (!resolvedTooltipTitle) return button

  return (
    <Tooltip title={resolvedTooltipTitle} arrow>
      <span>{button}</span>
    </Tooltip>
  )
}

export default HeaderExportButton
